import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

/**
 * Adapted from jquery.ripples v0.6.3 (MIT, Pim Schreurs).
 * The jQuery wrapper is removed and the engine is driven by this custom element.
 */

type RippleConfig = {
	type: number
	arrayType: Float32ArrayConstructor | null
	linearSupport: boolean
	extensions: string[]
}

type RippleProgram = {
	id: WebGLProgram
	locations: Record<string, WebGLUniformLocation | null>
	uniforms: Record<string, Float32Array>
}

const TRANSPARENT_PIXELS = (() => {
	try {
		return new ImageData(32, 32)
	} catch {
		const canvas = document.createElement('canvas')
		return canvas.getContext('2d')!.createImageData(32, 32)
	}
})()

function isPercentage(str: string) {
	return str[str.length - 1] === '%'
}

function isDataUri(url: string) {
	return /^data:/.test(url)
}

function translateBackgroundPosition(value: string) {
	const parts = value.split(' ')

	if (parts.length === 1) {
		switch (value) {
			case 'center':
				return ['50%', '50%']
			case 'top':
				return ['50%', '0']
			case 'bottom':
				return ['50%', '100%']
			case 'left':
				return ['0', '50%']
			case 'right':
				return ['100%', '50%']
			default:
				return [value, '50%']
		}
	}

	return parts.map((part) => {
		switch (part) {
			case 'center':
				return '50%'
			case 'top':
			case 'left':
				return '0'
			case 'right':
			case 'bottom':
				return '100%'
			default:
				return part
		}
	})
}

function loadConfig() {
	const canvas = document.createElement('canvas')
	const gl =
		canvas.getContext('webgl') ||
		canvas.getContext('experimental-webgl')

	if (!gl) return null

	const extensions: Record<string, any> = {}
	;[
		'OES_texture_float',
		'OES_texture_half_float',
		'OES_texture_float_linear',
		'OES_texture_half_float_linear',
	].forEach((name) => {
		const extension = gl.getExtension(name)
		if (extension) extensions[name] = extension
	})

	if (!extensions.OES_texture_float) return null

	const configs: RippleConfig[] = [
		{
			type: gl.FLOAT,
			arrayType: Float32Array,
			linearSupport: 'OES_texture_float_linear' in extensions,
			extensions: ['OES_texture_float'].concat(
				'OES_texture_float_linear' in extensions
					? ['OES_texture_float_linear']
					: []
			),
		},
	]

	if (extensions.OES_texture_half_float) {
		configs.push({
			type: extensions.OES_texture_half_float.HALF_FLOAT_OES,
			arrayType: null,
			linearSupport: 'OES_texture_half_float_linear' in extensions,
			extensions: ['OES_texture_half_float'].concat(
				'OES_texture_half_float_linear' in extensions
					? ['OES_texture_half_float_linear']
					: []
			),
		})
	}

	const texture = gl.createTexture()
	const framebuffer = gl.createFramebuffer()
	if (!texture || !framebuffer) return null

	gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
	gl.bindTexture(gl.TEXTURE_2D, texture)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

	for (const config of configs) {
		gl.texImage2D(
			gl.TEXTURE_2D,
			0,
			gl.RGBA,
			32,
			32,
			0,
			gl.RGBA,
			config.type,
			null
		)

		gl.framebufferTexture2D(
			gl.FRAMEBUFFER,
			gl.COLOR_ATTACHMENT0,
			gl.TEXTURE_2D,
			texture,
			0
		)

		if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE) {
			return config
		}
	}

	return null
}

export class RippleHero extends UcoastEl {
	static htmlSelector = 'ripple-hero'

	private static config: RippleConfig | null | undefined

	private canvas?: HTMLCanvasElement
	private fallbackImage?: HTMLImageElement
	private gl?: WebGLRenderingContext
	private textures: WebGLTexture[] = []
	private framebuffers: WebGLFramebuffer[] = []
	private quad?: WebGLBuffer
	private backgroundTexture?: WebGLTexture
	private dropProgram?: RippleProgram
	private updateProgram?: RippleProgram
	private renderProgram?: RippleProgram
	private bufferWriteIndex = 0
	private bufferReadIndex = 1
	private resolution = 512
	private dropRadius = 15
	private perturbance = 0.01
	private running = false
	private visible = false
	private destroyed = false
	private imageSource?: string
	private imageBounds = { width: 0, height: 0 }
	private textureDelta = new Float32Array([1 / 512, 1 / 512])
	private boundOnResize: () => void
	private boundOnPointerMove: (event: PointerEvent) => void
	private boundOnPointerDown: (event: PointerEvent) => void

	constructor() {
		super()
		this.boundOnResize = this.onResize.bind(this)
		this.boundOnPointerMove = this.onPointerMove.bind(this)
		this.boundOnPointerDown = this.onPointerDown.bind(this)
	}

	override async connectedCallback() {
		super.connectedCallback()

		if (this.dataset.ucRippleEnabled === 'false') return

		this.canvas = q.os<HTMLCanvasElement>('.hero-ripple__canvas', this)
		this.fallbackImage = q.os<HTMLImageElement>('[data-uc-fallback-image] img', this)
		if (!this.canvas || !this.fallbackImage) return

		if (RippleHero.config === undefined) {
			RippleHero.config = loadConfig()
		}
		if (!RippleHero.config) return

		const gl =
			this.canvas.getContext('webgl') ||
			this.canvas.getContext('experimental-webgl')
		if (!gl) return

		this.gl = gl
		RippleHero.config.extensions.forEach((name) => {
			gl.getExtension(name)
		})

		this.resolution = 512
		this.dropRadius = parseFloat(this.dataset.ucRippleRadius || '15')
		this.perturbance = parseFloat(this.dataset.ucRippleStrength || '5') * 0.002
		this.textureDelta = new Float32Array([
			1 / this.resolution,
			1 / this.resolution,
		])

		const imageReady = await this.ensureImageLoaded(this.fallbackImage)
		if (!imageReady) return

		const initialized = await this.initialize()
		if (!initialized) return

		window.addEventListener('resize', this.boundOnResize)
		this.addEventListener('pointermove', this.boundOnPointerMove, { passive: true })
		this.addEventListener('pointerdown', this.boundOnPointerDown, { passive: true })

		const step = () => {
			if (this.destroyed) return
			this.step()
			window.requestAnimationFrame(step)
		}

		window.requestAnimationFrame(step)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.destroyed = true
		window.removeEventListener('resize', this.boundOnResize)
		this.removeEventListener('pointermove', this.boundOnPointerMove)
		this.removeEventListener('pointerdown', this.boundOnPointerDown)
	}

	private async initialize() {
		if (!this.gl || !this.canvas || !this.fallbackImage || !RippleHero.config) {
			return false
		}

		this.destroyed = false
		this.visible = true
		this.running = true

		if (!this.quad) {
			this.initRipplesData()
			this.initShaders()
			this.initTexture()
		}

		this.gl.clearColor(0, 0, 0, 0)
		this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)

		this.updateSize()
		const imageLoaded = await this.loadImage()
		if (!imageLoaded) return false

		this.classList.add('is-enhanced')
		return true
	}

	private initRipplesData() {
		if (!this.gl || !RippleHero.config) return
		const gl = this.gl
		const arrayType = RippleHero.config.arrayType
		const textureData = arrayType
			? new arrayType(this.resolution * this.resolution * 4)
			: null

		this.textures = []
		this.framebuffers = []
		this.bufferWriteIndex = 0
		this.bufferReadIndex = 1

		for (let i = 0; i < 2; i++) {
			const texture = gl.createTexture()
			const framebuffer = gl.createFramebuffer()
			if (!texture || !framebuffer) continue

			gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
			gl.bindTexture(gl.TEXTURE_2D, texture)
			gl.texParameteri(
				gl.TEXTURE_2D,
				gl.TEXTURE_MIN_FILTER,
				RippleHero.config.linearSupport ? gl.LINEAR : gl.NEAREST
			)
			gl.texParameteri(
				gl.TEXTURE_2D,
				gl.TEXTURE_MAG_FILTER,
				RippleHero.config.linearSupport ? gl.LINEAR : gl.NEAREST
			)
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
			gl.texImage2D(
				gl.TEXTURE_2D,
				0,
				gl.RGBA,
				this.resolution,
				this.resolution,
				0,
				gl.RGBA,
				RippleHero.config.type,
				textureData as ArrayBufferView | null
			)
			gl.framebufferTexture2D(
				gl.FRAMEBUFFER,
				gl.COLOR_ATTACHMENT0,
				gl.TEXTURE_2D,
				texture,
				0
			)

			this.textures.push(texture)
			this.framebuffers.push(framebuffer)
		}

		this.quad = gl.createBuffer() ?? undefined
		if (!this.quad) return

		gl.bindBuffer(gl.ARRAY_BUFFER, this.quad)
		gl.bufferData(
			gl.ARRAY_BUFFER,
			new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]),
			gl.STATIC_DRAW
		)
	}

	private createProgram(vertexSource: string, fragmentSource: string) {
		if (!this.gl) throw new Error('WebGL not initialized')
		const gl = this.gl

		const compileSource = (type: number, source: string) => {
			const shader = gl.createShader(type)
			if (!shader) throw new Error('shader create error')
			gl.shaderSource(shader, source)
			gl.compileShader(shader)
			if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
				throw new Error(`compile error: ${gl.getShaderInfoLog(shader)}`)
			}
			return shader
		}

		const id = gl.createProgram()
		if (!id) throw new Error('program create error')

		gl.attachShader(id, compileSource(gl.VERTEX_SHADER, vertexSource))
		gl.attachShader(id, compileSource(gl.FRAGMENT_SHADER, fragmentSource))
		gl.linkProgram(id)
		if (!gl.getProgramParameter(id, gl.LINK_STATUS)) {
			throw new Error(`link error: ${gl.getProgramInfoLog(id)}`)
		}

		const program: RippleProgram = {
			id,
			locations: {},
			uniforms: {},
		}

		gl.useProgram(id)
		gl.enableVertexAttribArray(0)
		const regex = /uniform (\w+) (\w+)/g
		let match: RegExpExecArray | null
		const shaderCode = `${vertexSource}\n${fragmentSource}`
		while ((match = regex.exec(shaderCode)) !== null) {
			program.locations[match[2]] = gl.getUniformLocation(id, match[2])
		}

		return program
	}

	private initShaders() {
		if (!this.gl) return
		const gl = this.gl

		const vertexShader = [
			'attribute vec2 vertex;',
			'varying vec2 coord;',
			'void main() {',
			'coord = vertex * 0.5 + 0.5;',
			'gl_Position = vec4(vertex, 0.0, 1.0);',
			'}',
		].join('\n')

		this.dropProgram = this.createProgram(
			vertexShader,
			[
				'precision highp float;',
				'const float PI = 3.141592653589793;',
				'uniform sampler2D texture;',
				'uniform vec2 center;',
				'uniform float radius;',
				'uniform float strength;',
				'varying vec2 coord;',
				'void main() {',
				'vec4 info = texture2D(texture, coord);',
				'float drop = max(0.0, 1.0 - length(center * 0.5 + 0.5 - coord) / radius);',
				'drop = 0.5 - cos(drop * PI) * 0.5;',
				'info.r += drop * strength;',
				'gl_FragColor = info;',
				'}',
			].join('\n')
		)

		this.updateProgram = this.createProgram(
			vertexShader,
			[
				'precision highp float;',
				'uniform sampler2D texture;',
				'uniform vec2 delta;',
				'varying vec2 coord;',
				'void main() {',
				'vec4 info = texture2D(texture, coord);',
				'vec2 dx = vec2(delta.x, 0.0);',
				'vec2 dy = vec2(0.0, delta.y);',
				'float average = (',
				'texture2D(texture, coord - dx).r +',
				'texture2D(texture, coord - dy).r +',
				'texture2D(texture, coord + dx).r +',
				'texture2D(texture, coord + dy).r',
				') * 0.25;',
				'info.g += (average - info.r) * 2.0;',
				'info.g *= 0.995;',
				'info.r += info.g;',
				'gl_FragColor = info;',
				'}',
			].join('\n')
		)
		gl.uniform2fv(this.updateProgram.locations.delta, this.textureDelta)

		this.renderProgram = this.createProgram(
			[
				'precision highp float;',
				'attribute vec2 vertex;',
				'uniform vec2 topLeft;',
				'uniform vec2 bottomRight;',
				'uniform vec2 containerRatio;',
				'varying vec2 ripplesCoord;',
				'varying vec2 backgroundCoord;',
				'void main() {',
				'backgroundCoord = mix(topLeft, bottomRight, vertex * 0.5 + 0.5);',
				'backgroundCoord.y = 1.0 - backgroundCoord.y;',
				'ripplesCoord = vec2(vertex.x, -vertex.y) * containerRatio * 0.5 + 0.5;',
				'gl_Position = vec4(vertex.x, -vertex.y, 0.0, 1.0);',
				'}',
			].join('\n'),
			[
				'precision highp float;',
				'uniform sampler2D samplerBackground;',
				'uniform sampler2D samplerRipples;',
				'uniform vec2 delta;',
				'uniform float perturbance;',
				'varying vec2 ripplesCoord;',
				'varying vec2 backgroundCoord;',
				'void main() {',
				'float height = texture2D(samplerRipples, ripplesCoord).r;',
				'float heightX = texture2D(samplerRipples, vec2(ripplesCoord.x + delta.x, ripplesCoord.y)).r;',
				'float heightY = texture2D(samplerRipples, vec2(ripplesCoord.x, ripplesCoord.y + delta.y)).r;',
				'vec3 dx = vec3(delta.x, heightX - height, 0.0);',
				'vec3 dy = vec3(0.0, heightY - height, delta.y);',
				'vec2 offset = -normalize(cross(dy, dx)).xz;',
				'float specular = pow(max(0.0, dot(offset, normalize(vec2(-0.6, 1.0)))), 4.0);',
				'gl_FragColor = texture2D(samplerBackground, backgroundCoord + offset * perturbance) + specular;',
				'}',
			].join('\n')
		)
		gl.uniform2fv(this.renderProgram.locations.delta, this.textureDelta)
	}

	private initTexture() {
		if (!this.gl) return
		const gl = this.gl
		this.backgroundTexture = gl.createTexture() ?? undefined
		if (!this.backgroundTexture) return
		gl.bindTexture(gl.TEXTURE_2D, this.backgroundTexture)
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
		this.setTransparentTexture()
	}

	private setTransparentTexture() {
		if (!this.gl || !this.backgroundTexture) return
		const gl = this.gl
		gl.bindTexture(gl.TEXTURE_2D, this.backgroundTexture)
		gl.texImage2D(
			gl.TEXTURE_2D,
			0,
			gl.RGBA,
			gl.RGBA,
			gl.UNSIGNED_BYTE,
			TRANSPARENT_PIXELS
		)
	}

	private bindTexture(texture?: WebGLTexture, unit = 0) {
		if (!this.gl || !texture) return
		const gl = this.gl
		gl.activeTexture(gl.TEXTURE0 + unit)
		gl.bindTexture(gl.TEXTURE_2D, texture)
	}

	private drawQuad() {
		if (!this.gl || !this.quad) return
		const gl = this.gl
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quad)
		gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
		gl.drawArrays(gl.TRIANGLE_FAN, 0, 4)
	}

	private async ensureImageLoaded(image: HTMLImageElement) {
		if (image.complete && image.naturalWidth > 0) {
			return image.decode
				? image.decode().then(() => true).catch(() => true)
				: Promise.resolve(true)
		}

		return new Promise<boolean>((resolve) => {
			const cleanup = () => {
				image.removeEventListener('load', onLoad)
				image.removeEventListener('error', onError)
			}
			const onLoad = () => {
				cleanup()
				resolve(true)
			}
			const onError = () => {
				cleanup()
				resolve(false)
			}
			image.addEventListener('load', onLoad, { once: true })
			image.addEventListener('error', onError, { once: true })
		})
	}

	private async loadImage() {
		if (!this.fallbackImage || !this.gl || !this.backgroundTexture) return false

		const source = this.fallbackImage.currentSrc || this.fallbackImage.src
		if (!source) {
			this.setTransparentTexture()
			return false
		}

		if (source === this.imageSource && this.imageBounds.width && this.imageBounds.height) {
			return true
		}

		const image = new Image()
		image.crossOrigin = isDataUri(source) ? '' : 'anonymous'

		const loaded = await new Promise<boolean>((resolve) => {
			image.onload = () => resolve(true)
			image.onerror = () => resolve(false)
			image.src = source
		})

		if (!loaded) {
			this.setTransparentTexture()
			return false
		}

		const gl = this.gl
		const isPowerOfTwo = (value: number) => (value & (value - 1)) === 0
		const wrapping =
			isPowerOfTwo(image.width) && isPowerOfTwo(image.height)
				? gl.REPEAT
				: gl.CLAMP_TO_EDGE

		gl.bindTexture(gl.TEXTURE_2D, this.backgroundTexture)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapping)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapping)
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)

		this.imageSource = source
		this.imageBounds = { width: image.width, height: image.height }
		return true
	}

	private step() {
		if (!this.gl || !this.canvas || !this.renderProgram || !this.updateProgram) return
		if (!this.visible) return

		this.computeTextureBoundaries()
		if (this.running) this.update()
		this.render()
	}

	private render() {
		if (!this.gl || !this.canvas || !this.renderProgram || !this.backgroundTexture) return
		const gl = this.gl
		gl.bindFramebuffer(gl.FRAMEBUFFER, null)
		gl.viewport(0, 0, this.canvas.width, this.canvas.height)
		gl.enable(gl.BLEND)
		gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
		gl.useProgram(this.renderProgram.id)

		this.bindTexture(this.backgroundTexture, 0)
		this.bindTexture(this.textures[0], 1)

		gl.uniform1f(this.renderProgram.locations.perturbance, this.perturbance)
		gl.uniform2fv(this.renderProgram.locations.topLeft, this.renderProgram.uniforms.topLeft)
		gl.uniform2fv(
			this.renderProgram.locations.bottomRight,
			this.renderProgram.uniforms.bottomRight
		)
		gl.uniform2fv(
			this.renderProgram.locations.containerRatio,
			this.renderProgram.uniforms.containerRatio
		)
		gl.uniform1i(this.renderProgram.locations.samplerBackground, 0)
		gl.uniform1i(this.renderProgram.locations.samplerRipples, 1)

		this.drawQuad()
		gl.disable(gl.BLEND)
	}

	private update() {
		if (!this.gl || !this.updateProgram) return
		const gl = this.gl
		gl.viewport(0, 0, this.resolution, this.resolution)
		gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers[this.bufferWriteIndex] ?? null)
		this.bindTexture(this.textures[this.bufferReadIndex])
		gl.useProgram(this.updateProgram.id)
		this.drawQuad()
		this.swapBufferIndices()
	}

	private swapBufferIndices() {
		this.bufferWriteIndex = 1 - this.bufferWriteIndex
		this.bufferReadIndex = 1 - this.bufferReadIndex
	}

	private computeTextureBoundaries() {
		if (!this.renderProgram || !this.canvas || !this.fallbackImage) return

		const rect = this.getBoundingClientRect()
		const container = {
			left: rect.left + window.pageXOffset,
			top: rect.top + window.pageYOffset,
			width: rect.width,
			height: rect.height,
		}

		const backgroundPosition = translateBackgroundPosition(
			getComputedStyle(this.fallbackImage).objectPosition || '50% 50%'
		)

		const scale = Math.max(
			container.width / this.imageBounds.width,
			container.height / this.imageBounds.height
		)
		const backgroundWidth = this.imageBounds.width * scale
		const backgroundHeight = this.imageBounds.height * scale

		let backgroundX = backgroundPosition[0]
		let backgroundY = backgroundPosition[1]

		if (isPercentage(backgroundX)) {
			backgroundX = String(
				container.left +
					(container.width - backgroundWidth) * (parseFloat(backgroundX) / 100)
			)
		} else {
			backgroundX = String(container.left + parseFloat(backgroundX))
		}

		if (isPercentage(backgroundY)) {
			backgroundY = String(
				container.top +
					(container.height - backgroundHeight) * (parseFloat(backgroundY) / 100)
			)
		} else {
			backgroundY = String(container.top + parseFloat(backgroundY))
		}

		this.renderProgram.uniforms.topLeft = new Float32Array([
			(container.left - parseFloat(backgroundX)) / backgroundWidth,
			(container.top - parseFloat(backgroundY)) / backgroundHeight,
		])

		this.renderProgram.uniforms.bottomRight = new Float32Array([
			this.renderProgram.uniforms.topLeft[0] + container.width / backgroundWidth,
			this.renderProgram.uniforms.topLeft[1] + container.height / backgroundHeight,
		])

		const maxSide = Math.max(this.canvas.width, this.canvas.height)
		this.renderProgram.uniforms.containerRatio = new Float32Array([
			this.canvas.width / maxSide,
			this.canvas.height / maxSide,
		])
	}

	private drop(x: number, y: number, radius: number, strength: number) {
		if (!this.gl || !this.dropProgram) return
		const gl = this.gl
		const elementWidth = this.clientWidth
		const elementHeight = this.clientHeight
		const longestSide = Math.max(elementWidth, elementHeight)
		const normalizedRadius = radius / longestSide

		const dropPosition = new Float32Array([
			(2 * x - elementWidth) / longestSide,
			(elementHeight - 2 * y) / longestSide,
		])

		gl.viewport(0, 0, this.resolution, this.resolution)
		gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers[this.bufferWriteIndex] ?? null)
		this.bindTexture(this.textures[this.bufferReadIndex])
		gl.useProgram(this.dropProgram.id)
		gl.uniform2fv(this.dropProgram.locations.center, dropPosition)
		gl.uniform1f(this.dropProgram.locations.radius, normalizedRadius)
		gl.uniform1f(this.dropProgram.locations.strength, strength)
		this.drawQuad()
		this.swapBufferIndices()
	}

	private dropAtPointer(event: PointerEvent, big = false) {
		const rect = this.getBoundingClientRect()
		this.drop(
			event.clientX - rect.left,
			event.clientY - rect.top,
			this.dropRadius * (big ? 1.5 : 1),
			big ? 0.14 : 0.01
		)
	}

	private onPointerMove(event: PointerEvent) {
		if (!this.visible || !this.running) return
		this.dropAtPointer(event)
	}

	private onPointerDown(event: PointerEvent) {
		if (!this.visible || !this.running) return
		this.dropAtPointer(event, true)
	}

	private onResize() {
		this.updateSize()
		void this.loadImage()
	}

	private updateSize() {
		if (!this.canvas) return
		const width = this.clientWidth
		const height = this.clientHeight
		if (width !== this.canvas.width || height !== this.canvas.height) {
			this.canvas.width = width
			this.canvas.height = height
		}
	}
}
